/**
 * Channel Intelligence API client. Talks to /api/admin/channel-intelligence
 * (read-time joins of YouTube monthly + ad warehouse + earnings + metadata).
 */

import { apiRequest, API_BASE_URL, getAuthToken } from './api';
import type { DsAdMonthlyRollup } from './adminApi';

const BASE = '/api/admin/channel-intelligence';

// ---- types (mirror channelIntelligenceQueryService.ts) ----

export interface CiYouTubeMonth {
  month: string;
  views: number;
  watchTimeHours: number;
  subscribersGained: number;
  subscribersLost: number;
  netSubscribers: number;
  averageViewPercentage: number;
  likes: number;
  comments: number;
  shares: number;
}
export interface CiAdMonth {
  month: string;
  impressions: number;
  clicks: number;
  revenueUsd: number;
  advertiserCount: number;
}
export interface CiAdvertiser {
  advertiser: string;
  advertiserId: string;
  months: string[];
  impressions: number;
  clicks: number;
  revenueUsd: number;
  firstMonth: string;
  lastMonth: string;
}
export interface CiCampaign {
  gamOrderId: string;
  campaignName?: string;
  months: string[];
  impressions: number;
  recordCount: number;
}
export interface CiEarningsMonth {
  month: string;
  directSoldUsd: number;
  adsenseUsd: number;
  totalUsd: number;
  directSoldImpressions?: number;
}
export interface CiAgeGender { ageGroup: string; gender: string; viewerPercentage: number }
export interface CiGeography { country: string; views: number; estimatedMinutesWatched: number }
export interface CiDevice { deviceType: string; views: number }
export interface CiTrafficSource { source: string; views: number; estimatedMinutesWatched: number }

export interface ChannelIntelligenceProfile {
  channelId: string;
  channelName: string;
  metadata: {
    category?: string;
    cms?: string;
    driveFolderId?: string;
    trackerSheetId?: string;
    mappingPresent: boolean;
    nameSource: string;
  };
  youtube: {
    months: CiYouTubeMonth[];
    totals: { views: number; watchTimeHours: number; netSubscribers: number };
    latestAudience?: {
      month: string;
      ageGender?: CiAgeGender[];
      geography?: CiGeography[];
      devices?: CiDevice[];
      trafficSources?: CiTrafficSource[];
    };
    source: 'youtubeChannelMonthly' | 'none';
  };
  ads: {
    months: CiAdMonth[];
    advertisers: CiAdvertiser[];
    campaigns: CiCampaign[];
    totals: { impressions: number; clicks: number; revenueUsd: number; advertiserCount: number; campaignCount: number };
  };
  earnings: {
    months: CiEarningsMonth[];
    totals: { directSoldUsd: number; adsenseUsd: number; totalUsd: number };
  };
  coverage: { from: string; to: string; adMonths: number; ytMonths: number; earningsMonths: number };
}

export interface ChannelIntelligenceListItem {
  channelId: string;
  channelName: string;
  category?: string;
  hasEarnings: boolean;
  hasDirectSold: boolean;
  hasAdSense: boolean;
  monthCount: number;
  lastMonth?: string;
}

export interface NetworkOverview {
  month: string;
  ads: DsAdMonthlyRollup | null;
  youtube: {
    channelsWithData: number;
    totals: { views: number; watchTimeHours: number; netSubscribers: number };
    top: Array<{ channelId: string; channelName?: string; views: number; netSubscribers: number; watchTimeHours: number }>;
    source: 'youtubeChannelMonthly' | 'none';
  };
  availableYoutubeMonths: string[];
}

// ---- calls ----

export async function listChannelIntelligenceChannels(query?: string, limit = 200): Promise<{ channels: ChannelIntelligenceListItem[] }> {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  params.set('limit', String(limit));
  return apiRequest(`${BASE}/channels?${params.toString()}`);
}

export async function getChannelIntelligenceProfile(channelId: string, from?: string, to?: string): Promise<{ profile: ChannelIntelligenceProfile }> {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();
  return apiRequest(`${BASE}/channel/${encodeURIComponent(channelId)}${qs ? `?${qs}` : ''}`);
}

export async function getChannelIntelligenceOverview(month: string): Promise<{ overview: NetworkOverview }> {
  return apiRequest(`${BASE}/overview?month=${encodeURIComponent(month)}`);
}

// ---- Creator groups (multi-channel creators, e.g. iShowSpeed) ----

export interface CreatorGroupInfo {
  key: string;
  displayName: string;
  channels: Array<{ channelId: string; channelName: string }>;
}

export interface CreatorIntelligenceProfile {
  creatorKey: string;
  displayName: string;
  channels: Array<{ channelId: string; channelName: string }>;
  merged: {
    youtube: ChannelIntelligenceProfile['youtube'];
    ads: ChannelIntelligenceProfile['ads'];
    earnings: ChannelIntelligenceProfile['earnings'];
  };
  perChannel: ChannelIntelligenceProfile[];
  coverage: { from: string; to: string };
}

export interface LiveNowSession {
  videoId: string;
  title: string;
  channelId: string;
  channelName: string;
  creatorKey: string;
  actualStartTime?: string;
  lastConcurrentViewers: number;
  peakConcurrentViewers: number;
}

export async function listCreatorGroups(): Promise<{ creators: CreatorGroupInfo[] }> {
  return apiRequest(`${BASE}/creators`);
}

export async function getCreatorIntelligenceProfile(key: string, from?: string, to?: string): Promise<{ profile: CreatorIntelligenceProfile }> {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();
  return apiRequest(`${BASE}/creator/${encodeURIComponent(key)}${qs ? `?${qs}` : ''}`);
}

/** Streams currently live on monitored creator channels (empty unless the live poller is enabled). */
export async function getLiveNowSessions(): Promise<{ live: LiveNowSession[] }> {
  return apiRequest(`${BASE}/live-now`);
}

// ---- Channel CRM (imported Airtable "Network CRM" metadata) ----

export interface ChannelCrmRecord {
  channelId: string;
  displayName: string;
  channelLink?: string;
  logoUrl?: string; // signed Airtable URL — expires; used only as a fallback
  youtubeThumbnailUrl?: string; // stable YouTube avatar (preferred)
  youtubeThumbnailUpdatedAt?: string;
  channelSummary?: string;
  cms?: string;
  cmsSlug: string;
  categoryVertical: string[];
  subCategoryFormat: string[];
  contentType: string[];
  monetizationMethods: string[];
  audienceAppeal?: string;
  brandSafety?: string;
  propertyStatus?: string;
  contractStatus?: string;
  typeOfCreator?: string;
  spanishSpoken?: string;
  location?: string;
  topPerformingFormat?: string;
  allDefCastMember?: string;
  agency?: string;
  managers?: string;
  channelOwnerName?: string;
  cgPoc?: string;
  subscriberCount?: number;
  videoCount?: number;
  views?: number;
  watchTimeHours?: number;
  estimatedPartnerRevenue?: number;
  raw?: Record<string, string>;
  importRunId?: string;
  importedAt?: string;
  updatedAt?: string;
  manuallyEdited?: boolean;
}

export interface ChannelCrmImportResult {
  runId: string;
  total: number;
  written: number;
  skipped: number;
  importedAt: string;
  cmsReattribution?: string;
}

export async function listChannelCrm(params: { query?: string; category?: string; cms?: string; limit?: number } = {}): Promise<{
  records: ChannelCrmRecord[];
  total: number;
}> {
  const qs = new URLSearchParams();
  if (params.query) qs.set('query', params.query);
  if (params.category) qs.set('category', params.category);
  if (params.cms) qs.set('cms', params.cms);
  if (params.limit) qs.set('limit', String(params.limit));
  const s = qs.toString();
  return apiRequest(`${BASE}/crm${s ? `?${s}` : ''}`);
}

export interface ChannelCrmThumbnailRefreshResult {
  channelsScanned: number;
  resolved: number;
  updated: number;
  missing: number;
}

/** Re-resolve every channel's stable YouTube avatar and persist it (manage-only). */
export async function refreshChannelCrmThumbnails(): Promise<ChannelCrmThumbnailRefreshResult> {
  return apiRequest(`${BASE}/crm/refresh-thumbnails`, { method: 'POST' });
}

export async function getChannelCrmRecord(channelId: string): Promise<{ record: ChannelCrmRecord }> {
  return apiRequest(`${BASE}/crm/${encodeURIComponent(channelId)}`);
}

export async function patchChannelCrmRecord(channelId: string, patch: Partial<ChannelCrmRecord>): Promise<{ record: ChannelCrmRecord }> {
  return apiRequest(`${BASE}/crm/${encodeURIComponent(channelId)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/** Upload the Network CRM CSV (multipart). Uses a raw fetch so the browser sets the boundary. */
export async function importChannelCrmCsv(file: File): Promise<ChannelCrmImportResult> {
  const token = await getAuthToken();
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE_URL}${BASE}/crm/import`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` }, // no Content-Type — let the browser set multipart boundary
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Import failed' }));
    throw new Error(body.error || `Import failed (${res.status})`);
  }
  return res.json();
}
